import { type ReactNode, useRef } from 'react'
import { useT } from '@/session/useSession'
import { Drawer } from '../Drawer/Drawer'
import { Header } from '../Header/Header'
import { SidebarCollapsedProvider, useSidebarCollapsed } from '../Sidebar/useSidebarCollapsed'
import styles from './RootLayout.module.css'
import { useContentFocus } from './useContentFocus'
import { useContentScroll } from './useContentScroll'
import { useDrawer } from './useDrawer'

const MAIN_ID = 'main'

export type RootLayoutProps = {
  sidebar: (onNavigate: () => void) => ReactNode

  actions?: ReactNode
  profilePath: string
  profileLabelKey: string
  children: ReactNode
}

export function RootLayout({
  sidebar,
  actions,
  profilePath,
  profileLabelKey,
  children,
}: RootLayoutProps) {
  const t = useT()
  const content = useRef<HTMLElement>(null)
  const { collapsed, toggle } = useSidebarCollapsed()
  const drawer = useDrawer()

  useContentScroll(content)
  useContentFocus(content)

  return (
    <div className={styles.layout} data-collapsed={collapsed ? '' : undefined}>
      <a className={styles.skip} href={`#${MAIN_ID}`}>
        {t('layouts.main.skip_to_content')}
      </a>

      <aside className={styles.sidebar}>
        <SidebarCollapsedProvider collapsed={collapsed}>
          {sidebar(() => undefined)}
        </SidebarCollapsedProvider>
      </aside>
      <Drawer
        open={drawer.open}
        onOpenChange={drawer.setOpen}
        label={t('layouts.main.nav_label')}
        close={t('layouts.main.close_menu')}
      >
        {sidebar(drawer.close)}
      </Drawer>

      <div className={styles.main}>
        <Header
          actions={actions}
          collapsed={collapsed}
          onToggleRail={toggle}
          drawerOpen={drawer.open}
          onOpenDrawer={drawer.show}
          profilePath={profilePath}
          profileLabelKey={profileLabelKey}
        />
        <main id={MAIN_ID} ref={content} className={styles.content} tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  )
}
